import { usbDictaphoneSetupCardQueueItemAtom } from '@/atoms/launchModalsConfigAtom';
import { addDeviceDialogDevPreviewAtom } from '@/composition/hosts/dictation/device-setup-add/list-devices';
import { forceShowModalLocal } from '@/lib/modal-debugger';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { Button } from '@heidi/design-system/button';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import {
  dictaphoneSetupAwarenessDevTriggerOverrideAtom,
  dictaphoneSetupAwarenessDismissedKeysAtom,
  dictateAddDeviceDialogRequestAtom,
  dictateMapFunctionalityDialogRequestAtom,
} from '@heidi/dictate/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';

/** Preview device models — the card's hero art and copy follow the model. */
const MODEL_SEGMENTS: { value: string; label: string }[] = [
  { value: 'power_mic_4', label: 'PowerMic 4' },
  { value: 'power_mic_3', label: 'PowerMic III' },
  { value: 'speech_mike', label: 'SpeechMike' },
  { value: 'rec_mic', label: 'RecMic' },
];

const DEFAULT_MODEL = 'power_mic_4';

/** Fake hardware key for previewing the card without plugging a device in. */
const devDeviceKey = (model: string) => `${model}:1364:100:dev-preview`;

interface DeviceSetupAwarenessDevSectionProps {
  onRequestClose?: () => void;
}

export function DeviceSetupAwarenessDevSection({
  onRequestClose,
}: Readonly<DeviceSetupAwarenessDevSectionProps>) {
  const setDevTriggerOverride = useSetAtom(
    dictaphoneSetupAwarenessDevTriggerOverrideAtom
  );
  const setDismissedKeys = useSetAtom(dictaphoneSetupAwarenessDismissedKeysAtom);
  const setAddDeviceRequest = useSetAtom(dictateAddDeviceDialogRequestAtom);
  const setMapRequest = useSetAtom(dictateMapFunctionalityDialogRequestAtom);
  const setDevPreview = useSetAtom(addDeviceDialogDevPreviewAtom);
  const [model, setModel] = useState(DEFAULT_MODEL);
  // Same atom the host's useQueuedModal listens on.
  const cardModalId = useAtomValue(usbDictaphoneSetupCardQueueItemAtom).id;

  const isNonProd = getEnvironment() !== ENV_NAME.PROD;

  const handleShow = useCallback(() => {
    setDevTriggerOverride(devDeviceKey(model));
    forceShowModalLocal(cardModalId);
    onRequestClose?.();
  }, [cardModalId, model, onRequestClose, setDevTriggerOverride]);

  const handleReset = useCallback(() => {
    setDevTriggerOverride(null);
    setDismissedKeys((prev) => {
      if (!(devDeviceKey(model) in prev)) return prev;
      const next = { ...prev };
      delete next[devDeviceKey(model)];
      return next;
    });
  }, [model, setDevTriggerOverride, setDismissedKeys]);

  if (!isNonProd) {
    return null;
  }

  return (
    <section
      aria-labelledby="dev-tools-device-setup-awareness-heading"
      className="mt-4 border-t border-border pt-4"
    >
      <TypographyP2SemiBold
        id="dev-tools-device-setup-awareness-heading"
        className="mb-1"
      >
        Device setup awareness
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Preview the dictaphone connect card without plugging in a device — the model picks
        the hero illustration. Reset clears the dismissal memory recorded for the preview
        device.
      </TypographyCaption>

      <div className="flex flex-col gap-3">
        {/* Vertical orientation lets the list fit its 2x2 grid height — the
            horizontal list is h-9 (one row), so a second grid row would
            overflow under the buttons below. */}
        <SegmentedControl value={model} onValueChange={setModel} orientation="vertical">
          <SegmentedControlList className="grid w-full grid-cols-2 gap-1">
            {MODEL_SEGMENTS.map((opt) => (
              <SegmentedControlTrigger
                key={opt.value}
                value={opt.value}
                data-testid={`dev-tools-device-setup-model-${opt.value}`}
              >
                {opt.label}
              </SegmentedControlTrigger>
            ))}
          </SegmentedControlList>
        </SegmentedControl>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="pro"
            size="sm"
            className="flex-1"
            data-testid="dev-tools-show-device-setup-awareness"
            onClick={handleShow}
          >
            Show card
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            data-testid="dev-tools-reset-device-setup-awareness"
            onClick={handleReset}
          >
            Reset preview device
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="dev-tools-show-add-device-dialog"
          onClick={() => {
            setDevPreview(null);
            setAddDeviceRequest({ deviceKey: devDeviceKey(model), source: 'dev' });
            onRequestClose?.();
          }}
        >
          Show add-device dialog
        </Button>
        <div className="flex flex-col gap-2">
          {(
            [
              ['searching', 'Searching'],
              ['one', 'One device'],
              ['many', 'Many devices'],
            ] as const
          ).map(([state, label]) => (
            <Button
              key={state}
              type="button"
              variant="outline"
              size="sm"
              data-testid={`dev-tools-show-add-device-${state}`}
              onClick={() => {
                setDevPreview({ state, model });
                setAddDeviceRequest({
                  deviceKey: state === 'many' ? null : devDeviceKey(model),
                  source: 'dev',
                });
                onRequestClose?.();
              }}
            >
              {label}
            </Button>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="dev-tools-show-map-functionality-dialog"
          onClick={() => {
            setMapRequest({ deviceKey: devDeviceKey(model) });
            onRequestClose?.();
          }}
        >
          Show map functionality dialog
        </Button>
      </div>
    </section>
  );
}
